import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { SiteShell } from "@/components/site-shell";
import { GenerateCta } from "@/components/generate-cta";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { DISCIPLINE_BY_SLUG } from "@/lib/disciplines";
import { loadPublishedGuidePage } from "@/lib/guides.functions";

export const Route = createFileRoute("/guides/$slug")({
  loader: async ({ params }) => {
    const page = await loadPublishedGuidePage({ data: { slug: params.slug } });
    if (!page) throw notFound();
    return page;
  },
  head: ({ loaderData }) => {
    const guide = loaderData?.guide;
    if (!guide) return {};
    const title = `${guide.title} — DealDraft`;
    const url = `https://dealdraft.pro/guides/${guide.slug}`;
    const image = guide.ogImage
      ? `https://dealdraft.pro${guide.ogImage}`
      : "https://dealdraft.pro/og-image.png";
    const meta: Array<Record<string, unknown>> = [
      { title },
      { name: "description", content: guide.description },
      { property: "og:title", content: title },
      { property: "og:description", content: guide.description },
      { property: "og:type", content: "article" },
      { property: "og:site_name", content: "DealDraft" },
      { property: "og:url", content: url },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: title },
      { name: "twitter:description", content: guide.description },
      {
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "Article",
          headline: guide.title,
          description: guide.description,
          datePublished: guide.publishDate,
          dateModified: guide.updatedDate,
          author: { "@type": "Person", name: guide.author },
          publisher: {
            "@type": "Organization",
            name: "DealDraft",
            url: "https://dealdraft.pro",
          },
          mainEntityOfPage: url,
          image,
        },
      },
    ];
    if (guide.ogImage) {
      meta.push(
        { property: "og:image", content: image },
        { name: "twitter:image", content: image },
      );
    }
    if (guide.faq.length > 0) {
      meta.push({
        "script:ld+json": {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: guide.faq.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        },
      });
    }
    return {
      meta,
      links: [{ rel: "canonical", href: url }],
    };
  },
  component: GuidePostPage,
});

function GuidePostPage() {
  const { guide, related } = Route.useLoaderData();
  const discipline = DISCIPLINE_BY_SLUG.get(guide.discipline);

  return (
    <SiteShell>
      <article className="mx-auto max-w-3xl px-6 pt-16 pb-12">
        <p className="text-sm text-muted-foreground">
          {format(parseISO(guide.publishDate), "MMM d, yyyy")} · {guide.readTime} min read
        </p>
        <h1 className="mt-3 text-4xl font-bold tracking-tight sm:text-5xl">{guide.title}</h1>
        <p className="mt-5 text-base text-muted-foreground sm:text-lg">{guide.description}</p>
        <div className="guide-prose mt-10" dangerouslySetInnerHTML={{ __html: guide.html }} />
      </article>

      {guide.faq.length > 0 ? (
        <section className="mx-auto max-w-3xl px-6 py-16">
          <h2 className="text-center text-2xl font-semibold tracking-tight sm:text-3xl">
            Common questions
          </h2>
          <Accordion type="single" collapsible className="mt-10">
            {guide.faq.map((f) => (
              <AccordionItem key={f.question} value={f.question}>
                <AccordionTrigger className="text-left text-base font-medium">
                  {f.question}
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground">{f.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      ) : null}

      <section className="mx-auto max-w-3xl px-6 pb-16">
        <h2 className="text-center text-sm font-semibold uppercase tracking-wide text-muted-foreground">
          Related
        </h2>
        <div className="mt-5 flex flex-wrap items-center justify-center gap-3">
          {discipline ? (
            <Link
              to="/for/$slug"
              params={{ slug: discipline.slug }}
              className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-foreground hover:text-foreground"
            >
              {discipline.label} proposals
            </Link>
          ) : null}
          {related.map((item) => (
            <Link
              key={item.slug}
              to="/guides/$slug"
              params={{ slug: item.slug }}
              className="rounded-full border border-border px-4 py-2 text-sm font-medium hover:border-foreground hover:text-foreground"
            >
              {item.title}
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-4xl px-6 pb-20 text-center">
        <div className="rounded-2xl bg-linear-to-br from-primary to-[oklch(0.466_0.207_268)] p-10 text-primary-foreground sm:p-14">
          <h2 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Put the clause in a real proposal.
          </h2>
          <p className="mt-3 text-primary-foreground/80">
            Generate a matching proposal and contract in about a minute.
          </p>
          <div className="mt-7">
            <GenerateCta
              size="lg"
              variant="secondary"
              className="h-12 rounded-lg px-7 text-base text-primary"
              type={guide.discipline}
            >
              Generate My Proposal Now →
            </GenerateCta>
          </div>
        </div>
      </section>
    </SiteShell>
  );
}
